"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { Button, ResponsiveWrapper, cn } from "@pcln/horizon";
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useAnimatedPlaceholder } from "../hooks/useAnimatedPlaceholder.js";
import { aiSearchInput } from "./AiSearchInput.variants.js";
const LINE_HEIGHT_PX = 24;
const MAX_ROWS_DEFAULT = 5;
function AiSearchInput({ value, defaultValue = '', onChange, onSubmit, placeholder, animatedPlaceholders, animatedPlaceholderTiming, maxRows = MAX_ROWS_DEFAULT, disabled = false, minSubmitLength = 1, submitDisabled = false, submitLabel = 'Search', className, id, 'aria-label': ariaLabel, 'aria-describedby': ariaDescribedBy, scrollIntoViewOnFocus = false, onFocus, onBlur }) {
    const generatedId = useId();
    const textareaId = id ?? generatedId;
    const rootRef = useRef(null);
    const textareaRef = useRef(null);
    const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
    const [isMultiLine, setIsMultiLine] = useState(false);
    const isControlled = void 0 !== value;
    const inputValue = isControlled ? value : uncontrolledValue;
    const hasAnimatedPlaceholders = Boolean(animatedPlaceholders?.some((text)=>text.length > 0));
    const showAnimatedPlaceholder = !disabled && 0 === inputValue.length && hasAnimatedPlaceholders;
    const { displayText, opacity } = useAnimatedPlaceholder(animatedPlaceholders, animatedPlaceholderTiming, showAnimatedPlaceholder);
    const styles = aiSearchInput({
        isMultiLine,
        disabled
    });
    const resolvedAriaLabel = ariaLabel ?? placeholder ?? animatedPlaceholders?.find((text)=>text.length > 0) ?? 'AI search input';
    const nativePlaceholder = showAnimatedPlaceholder ? '' : placeholder;
    const canSubmit = !submitDisabled && inputValue.trim().length >= minSubmitLength;
    const setValue = useCallback((nextValue)=>{
        if (!isControlled) setUncontrolledValue(nextValue);
        onChange?.(nextValue);
    }, [
        isControlled,
        onChange
    ]);
    const handleSubmit = useCallback(()=>{
        const trimmedValue = inputValue.trim();
        if (trimmedValue.length < minSubmitLength || disabled || submitDisabled) return;
        onSubmit?.(trimmedValue);
    }, [
        disabled,
        inputValue,
        minSubmitLength,
        onSubmit,
        submitDisabled
    ]);
    const handleChange = useCallback((event)=>{
        setValue(event.target.value);
    }, [
        setValue
    ]);
    const handleKeyDown = useCallback((event)=>{
        if ('Enter' === event.key && !event.shiftKey) {
            event.preventDefault();
            handleSubmit();
        }
    }, [
        handleSubmit
    ]);
    const handleFocus = useCallback((event)=>{
        onFocus?.(event);
        if (!scrollIntoViewOnFocus) return;
        const scrollTarget = rootRef.current ?? event.currentTarget;
        const prefersReducedMotion = 'function' == typeof window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const behavior = prefersReducedMotion ? 'auto' : 'smooth';
        requestAnimationFrame(()=>{
            requestAnimationFrame(()=>{
                scrollTarget.scrollIntoView({
                    block: 'start',
                    behavior
                });
            });
        });
    }, [
        onFocus,
        scrollIntoViewOnFocus
    ]);
    useEffect(()=>{
        const textarea = textareaRef.current;
        if (!textarea) return;
        const minHeight = LINE_HEIGHT_PX;
        const maxHeight = minHeight * maxRows;
        const isEmpty = 0 === inputValue.length;
        const rafId = requestAnimationFrame(()=>{
            textarea.style.height = 'auto';
            const contentHeight = textarea.scrollHeight;
            const hasMultipleLines = !isEmpty && contentHeight > minHeight;
            setIsMultiLine((previous)=>{
                if (isEmpty) return false;
                if (hasMultipleLines) return true;
                return previous;
            });
            textarea.style.height = `${Math.min(Math.max(contentHeight, minHeight), maxHeight)}px`;
        });
        return ()=>cancelAnimationFrame(rafId);
    }, [
        inputValue,
        maxRows,
        isMultiLine
    ]);
    return /*#__PURE__*/ jsx(ResponsiveWrapper, {
        children: /*#__PURE__*/ jsx("div", {
            ref: rootRef,
            className: cn(styles.root(), scrollIntoViewOnFocus && 'scroll-mt-2', className),
            "data-testid": "ai-search-input",
            children: /*#__PURE__*/ jsxs("div", {
                className: styles.content(),
                children: [
                    /*#__PURE__*/ jsxs("div", {
                        className: styles.inputWrapper(),
                        children: [
                            /*#__PURE__*/ jsx("textarea", {
                                ref: textareaRef,
                                id: textareaId,
                                value: inputValue,
                                onChange: handleChange,
                                onKeyDown: handleKeyDown,
                                onFocus: handleFocus,
                                onBlur: onBlur,
                                disabled: disabled,
                                rows: 1,
                                placeholder: nativePlaceholder,
                                "aria-label": resolvedAriaLabel,
                                "aria-describedby": ariaDescribedBy,
                                className: styles.textarea()
                            }),
                            showAnimatedPlaceholder && /*#__PURE__*/ jsx("div", {
                                "aria-hidden": true,
                                "data-testid": "ai-search-input-animated-placeholder",
                                className: styles.placeholderOverlay(),
                                style: {
                                    opacity,
                                    transition: `opacity ${animatedPlaceholderTiming?.fadeMs ?? 700}ms ease`
                                },
                                children: displayText
                            })
                        ]
                    }),
                    /*#__PURE__*/ jsx("div", {
                        className: styles.footer(),
                        children: /*#__PURE__*/ jsx(Button, {
                            type: "primary",
                            size: "sm",
                            buttonType: "button",
                            disabled: disabled || !canSubmit,
                            onClick: handleSubmit,
                            className: styles.submitButton(),
                            children: submitLabel
                        })
                    })
                ]
            })
        })
    });
}
export { AiSearchInput };
